import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useHistory } from 'react-router-dom';
import { isObject, upperCase } from 'lodash-es';
import classNames from 'classnames';
import { Icons as Icon } from '../Icons/Icons';
import { DropdownCheck } from 'components';

import { Modal } from 'components-antd';
import { AGENT, TEAM_ADMIN } from 'settings/constants/roles';
import { getUserId } from 'store/selectors/user';
import { routes } from 'settings/navigation/routes';

import { Avatar, Table, Tooltip } from 'components-antd';
import styles from './styles.module.scss';
import { Wrapper } from 'components';
import { getSymphonyColumns } from 'store/selectors/symphony';
import { ColumnsDropdown } from '../ColumnsDropdown';
import { getTitleFromValue } from '../Columns/helpers';
import { getIfExistThread, priceConverter } from 'helpers';
import { TasksModal } from './TasksModal';
import { ClientsModal } from './ClientsModal/ClientsModal';
import { OffersModal } from './OffersModal/OffersModal';
import { ListingsModal } from './ListingsModal';
import { getThreadsListSelector } from 'store/selectors/sockets/threads';
import { SOCKET_THREAD_TYPES } from 'settings/constants/sockets';
import {
  changeMessagesDrawerTypeAction,
  openMessagesDrawerAction,
} from 'store/actions/drawers/messages';
import { DRAWER_MESSAGES_TYPES } from 'settings/constants/drawers';
import { appManageClientDrawerAction } from 'store/actions/app';

export const Content = (props) => {
  const dispatch = useDispatch();
  const selectedColumns = useSelector(getSymphonyColumns);
  const threads = useSelector(getThreadsListSelector);
  const { data, isPending } = props;
  const history = useHistory();
  const userId = useSelector(getUserId);
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState<any>(null);
  const [detailsType, setDetailsType] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [openDropdownKey, setOpenDropdownKey] = useState(null);

  const handleScroll = () => {
    const container = containerRef?.current?.querySelector('.ant-table-body') as HTMLDivElement;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;

    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1); // slight buffer for float precision
  };

  useEffect(() => {
    const container = containerRef?.current?.querySelector('.ant-table-body') as HTMLDivElement;
    if (!container) return;

    handleScroll(); // call initially

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll); // in case size affects scroll bounds

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [containerRef, tableData]);

  const scrollLeft = () => {
    containerRef?.current
      ?.querySelector('.ant-table-body')
      ?.scrollBy({ left: -400, behavior: 'smooth' });
  };

  const scrollRight = () => {
    containerRef?.current
      ?.querySelector('.ant-table-body')
      ?.scrollBy({ left: 400, behavior: 'smooth' });
  };

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const onClickAgent = (agentId) => {
    const canViewProfile = agentId !== userId;
    if (canViewProfile) history.push(`${routes.memberProfile}/${agentId}`);
  };

  const getTitle = (title, first, last, type, key) => {
    const cardLabels = {
      activity: 'Activity',
      clients: 'Clients',
      listing: 'Listings',
      purchase: 'Purchases',
      dual: 'Dual',
    };
    return (
      <div
        className={classNames(
          styles.title,
          styles[type],
          { [styles.firstBorder]: first },
          { [styles.lastBorder]: last },
        )}
      >
        {first && (
          <div className={styles.first}>
            <Icon className={styles.icon} variant={type} />
            {cardLabels[type]}
          </div>
        )}
        {title}
        {last && (
          <div className={classNames(styles.last, styles[type])}>
            <ColumnsDropdown
              type={type}
              onVisibleChange={(visible) => {
                setTimeout(() => {
                  setOpenDropdownKey(visible ? key : null);
                }, 1);
              }}
            >
              <Icon className={styles.edit} variant={Icon.EDIT} />
            </ColumnsDropdown>
          </div>
        )}
      </div>
    );
  };

  const getValue = (key, value, first, last, type, index) => {
    const isNested = isObject(value?.[key]);
    const actualValue = isNested ? value?.[key]?.Value : value?.[key];
    const isDisabled = !actualValue;

    const colors = {
      activity: '251, 145, 58,',
      clients: '81, 191, 225,',
      listing: '146, 140, 218,',
      purchase: '0, 177, 82,',
      dual: '70, 115, 209,',
    };

    const generateOpacityArray = (index, length) => {
      const maxOpacity = 0.08;
      const minOpacity = 0.02;

      if (length <= 1) return maxOpacity;

      const opacityStep = (maxOpacity - minOpacity) / (length - 1);
      return (maxOpacity - index * opacityStep).toFixed(3);
    };

    return (
      <div
        onClick={() => {
          setDetailsType(key);
          setOpen(true);
          setRow(value);
        }}
        className={classNames(
          styles.value,
          styles[type],
          { [styles.firstMargin]: first },
          { [styles.lastMargin]: last },
          { [styles.disabled]: isDisabled },
        )}
        style={{
          backgroundColor: `rgba(${colors[type]} ${generateOpacityArray(
            index,
            tableData?.length,
          )})`,
        }}
      >
        <div className={styles.card}>
          {isNested ? (
            <>
              {value?.[key]?.Count ? (
                <>
                  {priceConverter(actualValue)}
                  <p className={styles.count}> ({value?.[key]?.Count})</p>
                </>
              ) : (
                '-'
              )}
            </>
          ) : (
            actualValue || '-'
          )}
        </div>
      </div>
    );
  };

  const getColumns = Object.keys(selectedColumns)?.flatMap((type) =>
    selectedColumns?.[type]?.map((key, index) => {
      const first = index === 0;
      const last = index === selectedColumns?.[type]?.length - 1;
      return {
        key,
        dataIndex: key,
        className: openDropdownKey === key ? styles.dropdownOpen : '',
        title: () => getTitle(getTitleFromValue(key, false), first, last, type, key),
        sorter: (a, b) => {
          const getValue = (item) => {
            const base = item?.[key];
            if (typeof base === 'object' && base !== null) {
              return base?.Value ?? 0;
            }
            return base ?? 0;
          };

          const valA = getValue(a);
          const valB = getValue(b);

          return valA - valB;
        },
        render: (_, value, index) => getValue(key, value, first, last, type, index),
      };
    }),
  );

  const columns = [
    {
      title: () => <div className={styles.title}>Team</div>,
      key: 'team',
      fixed: 'left',
      dataIndex: 'FirstName',
      sorter: (a, b) => {
        let name = `${a?.FirstName} ${a?.LastName}`;
        let secondName = `${b?.FirstName} ${b?.LastName}`;
        return name.localeCompare(secondName);
      },
      render: (_, { Id, FirstName, LastName, Title, AvatarUrl, TeamRole, IsActive }) => {
        const avatar = AvatarUrl;
        const placeholder = upperCase(`${FirstName?.[0] || ''}${LastName?.[0] || ''}`);
        return (
          <div
            className={classNames(styles.teamField, { [styles.inactive]: !IsActive })}
            onClick={() => onClickAgent(Id)}
          >
            <Avatar className={styles.avatar} size={32} src={avatar}>
              {placeholder}
            </Avatar>
            <div className={styles.details}>
              <div className={styles.name}>
                <p>
                  {FirstName} {LastName}
                  {Id === userId ? ' (Me)' : ''}
                </p>
                {TeamRole === TEAM_ADMIN ? (
                  <div className={styles.admin}>
                    <span>Admin</span>
                  </div>
                ) : null}
              </div>
              <span className={styles.label}>
                {Title?.Title || AGENT} {!IsActive ? '(Inactive)' : ''}
              </span>
            </div>
          </div>
        );
      },
    },
    ...getColumns,
  ];

  const renderDetails = () => {
    switch (detailsType) {
      case 'openTasks':
        return <TasksModal row={row} variant={'openTasks'} />;
      case 'dueTodayTasks':
        return <TasksModal row={row} variant={'dueToday'} />;
      case 'stuckTasks':
        return <TasksModal row={row} variant={'stuckTasks'} />;
      case 'overdueTasks':
        return <TasksModal row={row} variant={'overdueTasks'} />;
      case 'activeClients':
        return <ClientsModal row={row} />;
      case 'pendingClients':
        return <ClientsModal row={row} variant={'pendingClients'} />;
      case 'buyers':
        return <ClientsModal row={row} variant={'buyers'} />;
      case 'sellers':
        return <ClientsModal row={row} variant={'sellers'} />;
      case 'retentionClients':
        return <ClientsModal row={row} variant={'retention'} />;
      case 'nonKitRetentionClients':
        return <ClientsModal row={row} variant={'nonRetention'} />;
      case 'pendingTourRequests':
        return <OffersModal row={row} variant={'pendingTourRequests'} />;
      case 'openOffers':
        return <OffersModal row={row} variant={'openOffers'} />;
      case 'expiringOffers':
        return <OffersModal row={row} variant={'expiringOffers'} />;
      case 'pendingShowingRequests':
        return <OffersModal row={row} variant={'pendingShowingRequests'} />;
      case 'pendingSignatures':
        return <OffersModal row={row} variant={'pendingSignatures'} />;
      case 'preListing':
        return <ListingsModal row={row} variant={'preListing'} />;
      case 'officeExclusive':
        return <ListingsModal row={row} variant={'officeExclusive'} />;
      case 'comingSoon':
        return <ListingsModal row={row} variant={'comingSoon'} />;
      case 'activeListing':
        return <ListingsModal row={row} variant={'activeListing'} />;
      case 'onHold':
        return <ListingsModal row={row} variant={'onHold'} />;
      case 'underContractListing':
        return <ListingsModal row={row} variant={'underContractListing'} />;
      case 'preListingDual':
        return <ListingsModal row={row} variant={'preListingDual'} />;
      case 'officeExclusiveDual':
        return <ListingsModal row={row} variant={'officeExclusiveDual'} />;
      case 'comingSoonDual':
        return <ListingsModal row={row} variant={'comingSoonDual'} />;
      case 'activeListingDual':
        return <ListingsModal row={row} variant={'activeListingDual'} />;
      case 'onHoldDual':
        return <ListingsModal row={row} variant={'onHoldDual'} />;
      case 'underContractDual':
        return <ListingsModal row={row} variant={'underContractDual'} />;
      case 'clearToCloseDual':
        return <ListingsModal row={row} variant={'clearToCloseDual'} />;
      case 'underContractPurchase':
        return <ListingsModal row={row} variant={'underContractPurchase'} />;
      case 'clearToClose':
        return <ListingsModal row={row} variant={'clearToClose'} />;
      default:
        return null;
    }
  };

  const existThread = getIfExistThread(
    threads,
    [row?.Id, userId],
    (thread) => thread?.Type === SOCKET_THREAD_TYPES.CHAT,
  );

  const openMessageDrawer = () => {
    setOpen(false);
    dispatch(openMessagesDrawerAction(true));
    dispatch(
      changeMessagesDrawerTypeAction({
        type: existThread ? DRAWER_MESSAGES_TYPES.CHAT : DRAWER_MESSAGES_TYPES.NEW_MESSAGE,
        params: {
          threadId: existThread?.Id || null,
          participants: [
            {
              id: row.Id,
              name: `${row?.FirstName} ${row?.LastName || ''}`,
              value: row.Id,
              role: row?.TeamRole,
              avatarUrl: row?.AvatarUrl,
              firstName: row?.FirstName,
              lastName: row?.LastName,
            },
          ],
        },
      }),
    );
    dispatch(appManageClientDrawerAction(false));
  };

  const clientModals = [
    'activeClients',
    'pendingClients',
    'pendingClients',
    'buyers',
    'buyers',
    'sellers',
    'sellers',
    'retentionClients',
    'retention',
    'nonKitRetentionClients',
  ];

  const isClientsModal = clientModals.includes(detailsType);

  return (
    <div className={styles.symphonyPage}>
      <div className={styles.content}>
        <Wrapper isPending={isPending}>
          {showLeftArrow && (
            <span onClick={scrollLeft}>
              <Icon className={classNames(styles.arrow, styles.left)} variant={Icon.ARROW_LEFT} />
            </span>
          )}
          {showRightArrow && (
            <span onClick={scrollRight}>
              <Icon className={classNames(styles.arrow, styles.right)} variant={Icon.ARROW_RIGHT} />
            </span>
          )}
          <div className={styles.symphonyTableContainer} ref={containerRef}>
            <Table
              className={styles.symphonyTable}
              bordered
              columns={columns}
              dataSource={tableData}
              pagination={false}
              showSorterTooltip={false}
              scroll={{ x: 'max-content', y: '100%' }}
              sticky
            />
          </div>
          <Modal
            open={open}
            width={1002}
            footer={null}
            onCancel={() => {
              setOpen(false);
            }}
            destroyOnClose
            className={styles.detailsModal}
            closeIconStyles={styles.closeIconStyles}
          >
            <div className={styles.content}>
              <p className={styles.titleMain}>{getTitleFromValue(detailsType, isClientsModal)}</p>
              <div className={styles.card}>
                <div className={styles.row1}>
                  <Avatar size={32} src={row?.AvatarUrl}>
                    {`${row?.FirstName?.[0]?.toUpperCase()}${row?.LastName?.[0]?.toUpperCase()}`}
                  </Avatar>
                  <div>
                    <p
                      className={classNames(styles.p1, { [styles.cursor]: row?.Id !== userId })}
                      onClick={() => {
                        onClickAgent(row?.Id);
                      }}
                    >{`${row?.FirstName} ${row?.LastName}`}</p>
                    <p className={styles.p2}>{row?.TeamRole}</p>
                  </div>
                </div>
                {row?.Id !== userId && (
                  <div className={styles.row2}>
                    <Tooltip
                      placement="top"
                      title={'View Profile'}
                      overlayClassName={classNames(styles.tooltipSend, 'mosaikTooltip')}
                    >
                      <span onClick={() => onClickAgent(row?.Id)}>
                        <Icon className={styles.icon} variant={Icon.PERSON} />
                      </span>
                    </Tooltip>
                    <Tooltip
                      placement="top"
                      title={'Message'}
                      overlayClassName={classNames(styles.tooltipSend, 'mosaikTooltip')}
                    >
                      <span onClick={openMessageDrawer}>
                        <Icon className={styles.icon} variant={Icon.CHAT} />
                      </span>
                    </Tooltip>
                  </div>
                )}
              </div>
              <div className={styles.table}>{renderDetails()}</div>
            </div>
          </Modal>
        </Wrapper>
      </div>
    </div>
  );
};
